import { Container, Card, CardBody, Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { TERMS_VERSION } from "@/security/terms.catalog";
import styles from "./style.module.scss";

/**
 * Términos de uso públicos para el sistema de facturación electrónica EasyposCR.
 * Redactados según el estándar para plataformas de facturación electrónica de Costa Rica,
 * incluyendo la cláusula de que EasyposCR es únicamente una herramienta y que el usuario
 * es el único responsable de los datos ingresados en cada documento.
 */
const Terms = () => {
  const navigate = useNavigate();

  return (
    <Container className={styles.termsContainer}>
      <Card className={styles.termsCard}>
        <CardBody>
          <h1 className={styles.title}>Términos y Condiciones de Uso</h1>
          <p className={styles.meta}>
            EasyposCR · Sistema de Facturación Electrónica · Versión {TERMS_VERSION}
          </p>

          <p>
            Los presentes Términos y Condiciones de Uso (en adelante, los “Términos”) regulan el
            acceso y uso de la plataforma <b>EasyposCR</b> (en adelante, “la Plataforma” o “el
            Servicio”), un sistema de facturación electrónica que opera de conformidad con la
            normativa de la Dirección General de Tributación del Ministerio de Hacienda de la
            República de Costa Rica. Al registrarse y utilizar la Plataforma, el usuario (en
            adelante, “el Usuario”) declara haber leído, entendido y aceptado íntegramente estos
            Términos.
          </p>

          <h2>1. Objeto del servicio</h2>
          <p>
            EasyposCR es una <b>herramienta tecnológica</b> que permite a sus Usuarios generar,
            firmar, transmitir, recibir y administrar comprobantes electrónicos (facturas
            electrónicas, tiquetes electrónicos, notas de crédito, notas de débito y demás
            documentos autorizados) ante el Ministerio de Hacienda, así como gestionar
            información comercial relacionada. La Plataforma facilita el cumplimiento de las
            obligaciones formales de facturación, pero no sustituye la asesoría contable, legal o
            tributaria que el Usuario requiera.
          </p>

          <h2>2. EasyposCR como herramienta y exención de responsabilidad sobre los datos</h2>
          <p className={styles.highlight}>
            El Usuario reconoce y acepta expresamente que <b>EasyposCR es únicamente una
            herramienta</b> que habilita y facilita la emisión de comprobantes electrónicos. La
            responsabilidad sobre la <b>veracidad, exactitud, integridad y legalidad de los datos
            ingresados</b> en cada documento (montos, impuestos, tarifas, exoneraciones,
            identificaciones, descripciones, condiciones de venta, fechas y cualquier otro dato)
            recae <b>exclusivamente en el Usuario</b> en su condición de emisor. En consecuencia,
            el Usuario <b>libera de toda responsabilidad a EasyposCR</b>, a la empresa titular de
            la Plataforma, a sus socios, colaboradores y proveedores, por cualquier error,
            inconsistencia, omisión, contingencia, sanción, multa o perjuicio derivado de la forma
            en que fueron ingresados o configurados los datos de cada documento, siendo el Usuario
            el único obligado frente al Ministerio de Hacienda, sus clientes y terceros.
          </p>

          <h2>3. Obligaciones del Usuario como emisor electrónico</h2>
          <p>El Usuario se obliga a:</p>
          <ul>
            <li>
              Encontrarse debidamente inscrito ante el Ministerio de Hacienda y contar con la
              autorización, el certificado de firma digital (llave criptográfica) y las
              credenciales vigentes que correspondan.
            </li>
            <li>
              Ingresar información veraz, completa y actualizada, incluyendo su identificación,
              actividad económica y datos fiscales, antes de emitir cualquier comprobante.
            </li>
            <li>
              Revisar cada comprobante antes de su emisión y conservar los documentos electrónicos
              y sus respuestas de Hacienda por el plazo legal establecido.
            </li>
            <li>
              Utilizar la Plataforma conforme a la ley, absteniéndose de emitir documentos
              fraudulentos, falsos o con fines ilícitos.
            </li>
            <li>Custodiar sus credenciales de acceso y no compartirlas con terceros no autorizados.</li>
          </ul>

          <h2>4. Disponibilidad del servicio</h2>
          <p>
            EasyposCR realizará esfuerzos razonables para mantener la Plataforma disponible; sin
            embargo, el Servicio se brinda “tal cual” y puede sufrir interrupciones por
            mantenimiento, causas de fuerza mayor, fallas de terceros (incluyendo el Ministerio de
            Hacienda, proveedores de conectividad o de pago) o motivos ajenos a su control.
            EasyposCR no garantiza la disponibilidad ininterrumpida ni se responsabiliza por la
            indisponibilidad de servicios externos, como la plataforma ATV o los sistemas de
            recepción de Hacienda.
          </p>

          <h2>5. Tratamiento de datos personales</h2>
          <p>
            EasyposCR trata los datos personales y comerciales del Usuario y de sus clientes
            conforme a la Ley N.° 8968 de Protección de la Persona frente al Tratamiento de sus
            Datos Personales, con la finalidad de prestar el Servicio. El Usuario es responsable de
            contar con la legitimación necesaria para incorporar datos de terceros en la Plataforma.
          </p>

          <h2>6. Pagos y suscripción</h2>
          <p>
            El acceso a los planes de pago se factura de forma periódica según el plan
            seleccionado. Los pagos con tarjeta son procesados por nuestro socio certificado{" "}
            <b>ONVO Pay</b>. <b>EasyposCR no almacena ni tiene acceso a los datos completos de las
            tarjetas de crédito o débito</b> del Usuario: dicha información es capturada y
            resguardada de forma segura y cifrada directamente por ONVO Pay, cumpliendo con los
            estándares de la industria. La falta de pago podrá suspender o cancelar el acceso al
            Servicio.
          </p>

          <h2>7. Cancelación</h2>
          <p>
            El Usuario puede cancelar su suscripción en cualquier momento desde la administración de
            su cuenta. Al cancelar, mantendrá acceso hasta el final del ciclo de cobro vigente,
            luego del cual el Servicio se desactivará y no se realizarán nuevos cargos.
          </p>

          <h2>8. Propiedad intelectual</h2>
          <p>
            La Plataforma, su software, marcas, diseños y contenidos son propiedad de EasyposCR o de
            sus licenciantes. El Usuario recibe únicamente una licencia de uso limitada, no
            exclusiva e intransferible, mientras mantenga una suscripción activa.
          </p>

          <h2>9. Limitación de responsabilidad</h2>
          <p>
            En la máxima medida permitida por la ley, la responsabilidad total de EasyposCR frente
            al Usuario por cualquier reclamo relacionado con el Servicio se limitará al monto
            efectivamente pagado por el Usuario durante el mes anterior al hecho generador.
            EasyposCR no responderá por daños indirectos, lucro cesante, pérdida de datos o
            perjuicios derivados del uso o imposibilidad de uso de la Plataforma.
          </p>

          <h2>10. Modificaciones</h2>
          <p>
            EasyposCR podrá actualizar estos Términos. Los cambios se comunicarán a través de la
            Plataforma y la versión vigente se identificará por su número de versión. El uso
            continuado del Servicio implica la aceptación de la versión actualizada.
          </p>

          <h2>11. Ley aplicable y jurisdicción</h2>
          <p>
            Estos Términos se rigen por las leyes de la República de Costa Rica. Cualquier
            controversia se someterá a los tribunales competentes de Costa Rica.
          </p>

          <div className={styles.actions}>
            <Button color="primary" onClick={() => navigate(-1)}>
              Volver
            </Button>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
};

export default Terms;
